import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  FolderOpen, LogOut, Home, FileText, Download, Menu, X, ChevronRight, Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

export default function VendorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadFiles = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true });
      setProjectFiles(res.data);
    } catch {}
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen bg-[#FDFCFB]">
      <aside className={`${mobileOpen ? 'fixed inset-0 z-50' : 'hidden'} lg:block lg:relative lg:w-64 lg:border-r lg:border-[#E5E5E5] bg-white h-screen lg:sticky lg:top-0`}>
        {mobileOpen && <div className="absolute inset-0 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />}
        <div className="relative w-64 bg-white h-full flex flex-col">
          <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="S.A.I." className="h-8" />
              <div>
                <span className="text-sm font-bold">S.A.I.</span>
                <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Vendor Portal</span>
              </div>
            </a>
            <button className="lg:hidden" onClick={() => setMobileOpen(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="flex items-center gap-3 px-6 py-4 border-b border-[#E5E5E5]">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-[#E5E5E5] flex items-center justify-center text-xs font-bold">
                {user?.name?.charAt(0) || '?'}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[10px] text-[#5C5C5C] truncate">{user?.email}</p>
            </div>
          </div>

          <div className="flex-1 py-4">
            <button data-testid="vendor-nav-projects"
              className="w-full flex items-center gap-3 px-6 py-3 text-sm bg-[#121212] text-white">
              <FolderOpen size={18} strokeWidth={1.25} />
              <span className="tracking-wide">Assigned Projects</span>
            </button>
          </div>

          <div className="p-4 border-t border-[#E5E5E5]">
            <a href="/" className="flex items-center gap-3 px-6 py-2 text-sm text-[#5C5C5C] hover:text-[#121212]">
              <Home size={18} strokeWidth={1.25} />
              <span>Back to Home</span>
            </a>
            <button onClick={handleLogout} data-testid="vendor-logout-btn"
              className="w-full flex items-center gap-3 px-6 py-2 text-sm text-[#5C5C5C] hover:text-[#121212]">
              <LogOut size={18} strokeWidth={1.25} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="vendor-mobile-menu">
            <Menu size={24} />
          </button>
          <span className="text-sm font-bold">Vendor Portal</span>
          <div />
        </div>

        <div className="p-6 lg:p-10 max-w-5xl">
          <div data-testid="vendor-projects">
            <div className="flex items-center gap-3 mb-6">
              <Building2 size={20} strokeWidth={1.25} />
              <h2 className="text-xl font-bold">Assigned Projects</h2>
            </div>

            {projects.length === 0 ? (
              <div className="border border-[#E5E5E5] bg-white p-12 text-center">
                <FolderOpen size={40} className="mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
                <p className="text-sm text-[#5C5C5C]">No projects assigned to you yet.</p>
                <p className="text-xs text-[#5C5C5C] mt-1">The admin will assign relevant projects for your work.</p>
              </div>
            ) : (
              <div className="space-y-1">
                {projects.map(proj => (
                  <div key={proj.project_id} className="border border-[#E5E5E5] bg-white">
                    <div className="p-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                      onClick={() => {
                        const isOpen = selectedProject === proj.project_id;
                        setSelectedProject(isOpen ? null : proj.project_id);
                        if (!isOpen) loadFiles(proj.project_id);
                      }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold">{proj.title}</p>
                          <p className="text-xs text-[#5C5C5C] mt-1">{proj.description}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="rounded-none text-[10px] uppercase">{proj.status}</Badge>
                          <ChevronRight size={16} className={`transition-transform ${selectedProject === proj.project_id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </div>

                    {selectedProject === proj.project_id && (
                      <div className="border-t border-[#E5E5E5] p-4">
                        <p className="text-xs font-bold uppercase tracking-wider mb-3">Design Files</p>
                        {projectFiles.length === 0 ? (
                          <p className="text-xs text-[#5C5C5C]">No files uploaded yet</p>
                        ) : (
                          <div className="space-y-2">
                            {projectFiles.map(f => (
                              <div key={f.file_id} className="flex items-center justify-between py-2 border-b border-[#E5E5E5] last:border-0">
                                <div className="flex items-center gap-2">
                                  <FileText size={14} className="text-[#5C5C5C]" />
                                  <span className="text-xs">{f.original_filename}</span>
                                  <span className="text-[10px] text-[#5C5C5C]">
                                    {(f.size / (1024 * 1024)).toFixed(2)} MB
                                  </span>
                                </div>
                                <Button variant="ghost" size="sm"
                                  data-testid={`vendor-download-${f.file_id}`}
                                  onClick={() => downloadFile(f.file_id, f.original_filename)}>
                                  <Download size={14} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
